/**
 * @author Hiroki Moto
 * @description https://marvelapp.com/4g4b5dg/screen/55348072
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import clone from 'ramda/src/clone';
import equals from 'ramda/src/equals';
import find from 'ramda/src/find';
import findIndex from 'ramda/src/findIndex';
import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';
import length from 'ramda/src/length';
import move from 'ramda/src/move';
import propEq from 'ramda/src/propEq';
import reverse from 'ramda/src/reverse';
import remove from 'ramda/src/remove';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Accordion from '@components/@core/Accordion';
import Button from '@components/@core/AnimatedButton';
import Dropdown from '@components/@core/Dropdown';
import Input from '@components/@core/Input';
import Filter from '@components/@core/Filter';
import { PerformanceReviewType, ScoreCardTypes } from '~/services/config';
import Quota from './Quota';

class Add extends Component {
    state = {
        title: '',
        description: '',
        selected: { author: [], department: [], competency: [], category: [], type: [0] },
        quotas: [],
        review: [],
        visible: false,
    };

    static getDerivedStateFromProps(nextProps, prevState) {
        const { modalData } = nextProps;
        if (modalData.after) {
            const { quotas } = modalData.after;
            if (quotas) {
                if (!equals(quotas, prevState.quotas)) {
                    return { quotas: quotas, visible: true };
                }
            }
        }
        return null;
    }

    componentDidUpdate(prevState) {
        const { visible } = this.state;
        if (visible && !prevState.visible) {
            this.handleAddQuotas();
            this.setState({ visible: false });
        }
    }

    handleAddQuotas = () => {
        const { quotas, selected } = this.state;
        this.props.onModal({
            type: 'Attach Library',
            data: { before: { show: ['quotas'], modules: [], selected: quotas, quotaType: selected.type[0] }, after: quotas },
            callBack: {
                onAttach: (e) => this.setState({ quotas: e.templates }),
                onAdd: () => this.handleNewQuota(),
            },
        });
    };

    handleNewQuota = () => {
        const { quotas } = this.state;
        this.props.onModal({ type: 'Add New Quota', data: { after: { quotas } } });
    };

    handleChangeQuota = (ind) => (type, e) => {
        if (type == 'stars') {
            const quota = this.state.quotas[ind];
            this.props.onModal({
                type: 'Add Quota Stars',
                data: {
                    before: {
                        title: quota.name,
                        unit: quota.data.target_types,
                        direction: quota.quota_direction,
                    },
                    after: e,
                },
                callBack: {
                    onYes: (stars) => {
                        const quotas = clone(this.state.quotas);
                        quotas[ind].stars = stars;
                        this.setState({ quotas });
                    },
                },
            });
        }
        if (type == 'type') {
            const quotas = clone(this.state.quotas);
            quotas[ind].data.target_types = e.name;
            this.setState({ quotas });
        }
        if (type == 'source') {
            const quotas = clone(this.state.quotas);
            quotas[ind].data.source = e.name;
            this.setState({ quotas });
        }
    };

    handleSelectTags = (key) => (tags) => {
        const { selected } = this.state;
        if (!equals(selected[key], tags)) {
            selected[key] = tags;
            this.setState({ selected });
        }
    };

    handleSelectMenu = (quota) => (event) => {
        const quotaLen = length(this.state.quotas);
        let quotas = clone(this.state.quotas);
        const indx = findIndex(propEq('id', quota.id), quotas);
        if (indx < 0) return;

        switch (event) {
            case 'move up':
                if (indx !== 0) quotas = move(indx, indx - 1, quotas);
                break;
            case 'move down':
                if (quotaLen !== indx + 1) quotas = move(indx, indx + 1, quotas);
                break;
            case 'remove':
                quotas = remove(indx, 1, quotas);
                break;
            case 'edit':
                const e = quota.star_values || quota.stars;
                this.props.onModal({
                    type: 'Add Quota Stars',
                    data: {
                        before: {
                            title: quota.name,
                            unit: quota.data.target_types,
                            direction: quota.quota_direction,
                        },
                        after: e,
                    },
                    callBack: {
                        onYes: (stars) => {
                            quotas[indx].star_values = stars;
                            quotas[indx].stars = stars;
                            this.setState({ quotas });
                        },
                    },
                });
                break;
            default:
                break;
        }

        this.setState({ quotas });
    };

    handleSubmit = (published) => () => {
        const { companyId } = this.props;
        const { title, description, selected, quotas } = this.state;
        let isValid = true;
        const noStars = find((x) => isNil(x.stars), quotas);
        if (isEmpty(title) || isEmpty(description) || isEmpty(selected.author) || isEmpty(quotas)) {
            toast.error('You missed something among title, description, author and quotas. \nPlease add them and then try it again.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        if (!!noStars && published) {
            toast.error('We can not publish any quotas with 0 stars. \nPlease add quotas with stars and then try it again.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }

        const _quotas = quotas.map((quota) => {
            let stars = quota.stars;
            if (isNil(stars)) {
                isValid = false;
            } else {
                if (equals(quota.quota_direction, 1) && quota.stars[0] > quota.stars[1]) {
                    stars = reverse(quota.stars);
                }
                if (equals(quota.quota_direction, 2) && quota.stars[0] < quota.stars[1]) {
                    stars = reverse(quota.stars);
                }
            }
            return {
                quota_template_id: quota.id,
                star_values: stars,
            };
        });

        if (!isValid && published) {
            toast.error('You missed quota scale. \nPlease add the scale and then try it again.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }

        const data = {
            scorecard: {
                title,
                published,
                type: selected.type[0],
                company_id: companyId,
                author_id: selected.author[0],
                data: {
                    description,
                    category: selected.category,
                    competency: selected.competency,
                    department: selected.department,
                    quotas: _quotas,
                },
            },
        };
        const payload = { type: 'scorecards', data };
        this.props.onCreate(payload);
    };

    render() {
        const { title, description, selected, quotas } = this.state;
        const { authors, departments, competencies, categories } = this.props;
        const disabled = !title || !description;

        return (
            <>
                <Filter back="all scorecards" />
                <div className="lib-todo-scorecard" data-cy="addScorecardForm">
                    <div className="scorecard-detail">
                        <div className="d-flex py-2">
                            <p className="dsl-b18 bold mb-0">Add Scorecard</p>
                        </div>
                        <div className="my-2">
                            <Dropdown className="dropdown-field" title="Scorecard type" direction="vertical" width="fit-content" data={ScoreCardTypes} onChange={this.handleSelectTags('type')} />
                        </div>
                        <Input
                            className="input-field"
                            title="Scorecard name"
                            dataCy="scorecardName"
                            type="text"
                            direction="vertical"
                            value={title}
                            placeholder="Type here..."
                            onChange={(e) => this.setState({ title: e })}
                        />
                        <Input
                            title="Scorecard description"
                            dataCy="scorecardDescription"
                            className="input-field input-textarea"
                            type="text"
                            value={description}
                            as="textarea"
                            rows="2"
                            direction="vertical"
                            placeholder="Type here..."
                            onChange={(e) => this.setState({ description: e })}
                        />

                        <Accordion className="settings-scorecard" icon="fas fa-gear" expanded>
                            <Row className="mt-1">
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        className="dropdown-field"
                                        title="Author"
                                        direction="vertical"
                                        dataCy="author"
                                        width="fit-content"
                                        data={authors}
                                        getValue={(e) => e.name}
                                        onChange={this.handleSelectTags('author')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field"
                                        title="Categories"
                                        direction="vertical"
                                        dataCy="categories"
                                        width="fit-content"
                                        data={categories}
                                        getValue={(e) => e.name}
                                        onChange={this.handleSelectTags('category')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field"
                                        title="Competencies"
                                        direction="vertical"
                                        dataCy="competencies"
                                        width="fit-content"
                                        data={competencies}
                                        getValue={(e) => e.name}
                                        onChange={this.handleSelectTags('competency')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        multi
                                        className="dropdown-field"
                                        title="Departments"
                                        direction="vertical"
                                        dataCy="department"
                                        width="fit-content"
                                        data={departments}
                                        getValue={(e) => e.name}
                                        onChange={this.handleSelectTags('department')}
                                    />
                                </Col>
                                <Col xs={12} sm={6} className="my-3">
                                    <Dropdown
                                        className="dropdown-field"
                                        dataCy="performanceReview"
                                        title="Trigger performance review"
                                        direction="vertical"
                                        width="fit-content"
                                        defaultIndexes={[0]}
                                        data={PerformanceReviewType}
                                    />
                                </Col>
                            </Row>
                        </Accordion>
                    </div>
                    <div className="detail-list mt-3" data-cy="quotaLists">
                        <div className="scorecard-quota-head">
                            <p className="dsl-b22 bold">Goals</p>
                            <div className="d-flex jc-end px-3">
                                <Button className="px-0 my-0 auto-height" type="link" name="+ Attach Goal" onClick={this.handleAddQuotas} />
                            </div>
                        </div>
                        <div className="card-item pt-2 py-3 me-2 desktop-screen">
                            <div className="text-left me-3">
                                <p className="dsl-m12 text-400 m-0">#</p>
                            </div>
                            <div className="d-flex-6 d-flex-ssm-1">
                                <p className="dsl-m12 text-400 m-0" data-cy="quotaLength">
                                    Quotas ({quotas.length})
                                </p>
                            </div>
                            <div className="d-flex-1 d-flex jc-center">
                                <p className="dsl-m12 text-400 m-0 ">Target</p>
                            </div>
                            <div className="d-flex-1 d-flex jc-center">
                                <p className="dsl-m12 text-400 m-0">Actual</p>
                            </div>
                            <div className="d-flex-2 d-flex jc-center">
                                <p className="dsl-m12 text-400 m-0">Scale</p>
                            </div>
                        </div>
                        {quotas?.map((quota, index) => (
                            <Quota editable data={quota} dataCy="quotaList" key={quota.id} onChange={this.handleChangeQuota(index)} onSelect={this.handleSelectMenu(quota)} />
                        ))}
                        <div className="d-flex jc-end mt-3">
                            <Button className="btn-save me-3" name="Save Draft" disabled={disabled} onClick={this.handleSubmit(0)} />
                            <Button className="btn-save" name="Save & Publish" disabled={disabled} onClick={this.handleSubmit(1)} />
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

Add.propTypes = {
    tags: PropTypes.array,
    companyId: PropTypes.number,
    onCreate: PropTypes.func,
    onModal: PropTypes.func,
};

Add.defaultProps = {
    tags: [],
    companyId: 0,
    onCreate: () => {},
    onModal: () => {},
};

export default Add;
